/**
 * Shared flag: "something the user MUST answer is on screen right now, so the
 * boot loading screen has to step aside".
 *
 * Today the only blocker is the confidentiality-disclaimer modal: it can appear
 * while the platform is still booting, and the loading screen would otherwise
 * cover a dialog the user has to accept before anything else can happen.
 *
 * Kept in its own tiny module (rather than inside the overlay component) so the
 * components that raise a blocker don't have to import the overlay itself.
 */

const blockers = new Set<string>();
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A broken listener must never break the caller.
    }
  });
};

/**
 * Raise or clear a blocker. `reason` identifies the caller (e.g. 'disclaimer')
 * so two blockers can't cancel each other out.
 */
export const setBootOverlayBlocked = (reason: string, blocked: boolean): void => {
  const wasBlocked = blockers.size > 0;
  if (blocked) {
    blockers.add(reason);
  } else {
    blockers.delete(reason);
  }
  if (wasBlocked !== (blockers.size > 0)) {
    notify();
  }
};

export const isBootOverlayBlocked = (): boolean => blockers.size > 0;

export const subscribeBootOverlayBlocked = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
